import { type BatchRegisterRow } from '@api/vendor-portal';
import { useCallback, useMemo, useState } from 'react';

import usePersistentState from '@hooks/usePersistentState';

import { dispatchQueue, groupQueueByProduct, type ProductGroup } from './shipmentRows';

/**
 * Picking batches for a shipment, on either side of the portal.
 *
 * ONE COPY, BECAUSE THERE WERE TWO. The site's own Finished Goods screen and the brand's view
 * of that site ran identical dispatch blocks — the same queue, the same picked set, the same
 * two toggle closures written out by hand, down to the same subtitle string. Nothing had
 * drifted yet, which is the only reason it was cheap to leave: the first fix applied to one
 * container and not the other is where a duplicated selection stops being harmless.
 *
 * THE MARKUP IS NOT HERE, deliberately. The two screens genuinely differ above this — the
 * site's button hides until something is ticked and counts what is, the brand's stays put and
 * greys out; one owns a tab strip and the other passes one down; and what to refetch afterwards
 * is each screen's own business. What they share is the bookkeeping, which is all this holds.
 *
 * See `useDispatchSelection` at the foot of this file.
 */

/**
 * Which grain the dispatch list is shown at.
 *
 * 'product' leads, because a manufacturer is told to send 400 units of a thing rather than to
 * send a batch code - and because one code can name two products, so the batch list asks people
 * to know something they have no reason to. 'batch' stays for whoever works from the codes.
 */
export type DispatchView = 'product' | 'batch';

export interface DispatchSelection {
    /** What could still go, FEFO-ordered: the rows the picker is drawn from. */
    queue: BatchRegisterRow[];
    /** The same queue as products, each carrying its batches in that FEFO order. */
    groups: ProductGroup[];
    view: DispatchView;
    setView: (view: DispatchView) => void;
    /** Batch ids currently ticked. */
    picked: Set<string>;
    /**
     * The ticked rows, IN THE QUEUE'S ORDER - soonest expiry first, undated last, oldest made
     * first on ties - which is the order the shipment builder lists them in.
     *
     * It used to read them off the register instead, which is newest-made first: the Dispatch
     * list then showed a product's batches in one order and the builder consumed them in the
     * opposite one. The ids can only have come from the queue, so the set is the same either
     * way; only the order changes, and the builder's "send this one first" depends on it.
     */
    pickedBatches: BatchRegisterRow[];
    /** Whether the shipment builder is open. */
    building: boolean;
    setBuilding: (open: boolean) => void;
    toggle: (row: BatchRegisterRow) => void;
    /** All of them, or none — whichever the visible rows are not already. */
    toggleAll: (rows: BatchRegisterRow[]) => void;
    clear: () => void;
    /** Ticking a product ticks every batch of it, which is what the shipment is built from. */
    toggleProduct: (group: ProductGroup) => void;
    toggleAllProducts: (groups: ProductGroup[]) => void;
    /** Whether every batch of this product is ticked. */
    isProductPicked: (group: ProductGroup) => boolean;
    /** How many products the selection covers, for the button that counts what it will send. */
    pickedProductCount: number;
    /** Ready to spread onto `BatchRegister`'s `selection` prop. */
    selection: {
        picked: Set<string>;
        onToggle: (row: BatchRegisterRow) => void;
        onToggleAll: (rows: BatchRegisterRow[]) => void;
    };
}

export function useDispatchSelection(
    batches: BatchRegisterRow[] | undefined,
): DispatchSelection {
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [building, setBuilding] = useState(false);

    /**
     * REMEMBERED, NOT RESET. Somebody who works from batch codes should not re-pick the batch
     * view every time they open the tab, and somebody who works from products should not have
     * their default quietly undone by a colleague's preference either - so it is per browser.
     */
    const [view, setView] = usePersistentState<DispatchView>(
        'reparo.dispatch.view',
        'product',
    );

    const all = useMemo(() => batches ?? [], [batches]);
    const queue = useMemo(() => dispatchQueue(all), [all]);
    const groups = useMemo(() => groupQueueByProduct(queue), [queue]);

    const pickedBatches = useMemo(
        () => queue.filter((batch) => picked.has(batch.batchId)),
        [queue, picked],
    );

    const toggle = useCallback((row: BatchRegisterRow) => {
        setPicked((current) => {
            const next = new Set(current);
            if (next.has(row.batchId)) next.delete(row.batchId);
            else next.add(row.batchId);
            return next;
        });
    }, []);

    const toggleAll = useCallback((rows: BatchRegisterRow[]) => {
        setPicked((current) =>
            rows.every((row) => current.has(row.batchId))
                ? new Set()
                : new Set(rows.map((row) => row.batchId)),
        );
    }, []);

    const clear = useCallback(() => setPicked(new Set()), []);

    /**
     * A PRODUCT IS TICKED WHEN ALL OF ITS BATCHES ARE. The shipment is built from batches
     * whichever view picked them, so the product tick is a shorthand for its own batches
     * rather than a second kind of selection to keep in step with the first.
     */
    const isProductPicked = useCallback(
        (group: ProductGroup) =>
            group.batches.length > 0 &&
            group.batches.every((batch) => picked.has(batch.batchId)),
        [picked],
    );

    const toggleProduct = useCallback((group: ProductGroup) => {
        setPicked((current) => {
            const next = new Set(current);
            const allOn = group.batches.every((batch) => next.has(batch.batchId));
            for (const batch of group.batches) {
                if (allOn) next.delete(batch.batchId);
                else next.add(batch.batchId);
            }
            return next;
        });
    }, []);

    const toggleAllProducts = useCallback((groups_: ProductGroup[]) => {
        setPicked((current) => {
            const every = groups_.flatMap((group) => group.batches);
            const allOn = every.every((batch) => current.has(batch.batchId));
            return allOn ? new Set() : new Set(every.map((batch) => batch.batchId));
        });
    }, []);

    const pickedProductCount = useMemo(
        () => groups.filter((group) => group.batches.some((b) => picked.has(b.batchId))).length,
        [groups, picked],
    );

    const selection = useMemo(
        () => ({ picked, onToggle: toggle, onToggleAll: toggleAll }),
        [picked, toggle, toggleAll],
    );

    return {
        queue,
        groups,
        view,
        setView,
        picked,
        pickedBatches,
        building,
        setBuilding,
        toggle,
        toggleAll,
        clear,
        toggleProduct,
        toggleAllProducts,
        isProductPicked,
        pickedProductCount,
        selection,
    };
}
